import { RefObject, useEffect } from 'react'

type UseStickyVideoPlayerCollapseAnimationArgs = Readonly<{
  sentinelRef: RefObject<HTMLElement | null>
  stickyRootRef: RefObject<HTMLElement | null>
  animatedContainerRef: RefObject<HTMLElement | null>

  collapsedMaxWidthPx: number
  collapsedPaddingPx: number

  /**
   * Start collapsing only after the sentinel has passed the top by this many px.
   * 0 means "start collapsing as soon as the sticky header reaches the top".
   */
  collapseStartOffsetPx?: number

  /**
   * How many px of scrolling it takes to go from expanded (0) to collapsed (1).
   * Larger = more gradual.
   */
  collapseDistancePx: number

  /**
   * 0..1 smoothing factor. Higher = snappier. Lower = floatier.
   * Ignored when prefers-reduced-motion is enabled.
   */
  smoothingFactor?: number
}>

const CSS_VARS = {
  backgroundAlpha: '--video-player-background-alpha',
  containerWidth: '--video-player-container-width',
  containerPadding: '--video-player-container-padding',
} as const

const clamp01 = (value: number): number => Math.min(1, Math.max(0, value))

const lerp = (from: number, to: number, t: number): number =>
  from + (to - from) * t

const easeOutCubic = (t: number): number => 1 - Math.pow(1 - t, 3)

const clampSmoothingFactor = (value: number): number => {
  if (!Number.isFinite(value)) return 0.18
  return Math.min(1, Math.max(0.01, value))
}

const addMediaQueryListener = (
  mediaQueryList: MediaQueryList,
  listener: () => void,
): (() => void) => {
  if (typeof mediaQueryList.addEventListener === 'function') {
    mediaQueryList.addEventListener('change', listener)
    return () => mediaQueryList.removeEventListener('change', listener)
  }

  mediaQueryList.addListener(listener)
  return () => mediaQueryList.removeListener(listener)
}

export const useStickyVideoPlayerCollapseAnimation = ({
  sentinelRef,
  stickyRootRef,
  animatedContainerRef,
  collapsedMaxWidthPx,
  collapsedPaddingPx,
  collapseStartOffsetPx = 0,
  collapseDistancePx,
  smoothingFactor = 0.18,
}: UseStickyVideoPlayerCollapseAnimationArgs): void => {
  useEffect(() => {
    const sentinel = sentinelRef.current
    const stickyRoot = stickyRootRef.current
    const animatedContainer = animatedContainerRef.current

    if (!sentinel || !stickyRoot || !animatedContainer) return

    const safeCollapseDistancePx = Math.max(1, collapseDistancePx)
    const safeCollapsedMaxWidthPx = Math.max(0, collapsedMaxWidthPx)
    const safeCollapsedPaddingPx = Math.max(0, collapsedPaddingPx)
    const safeSmoothingFactor = clampSmoothingFactor(smoothingFactor)

    const prefersReducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)',
    )
    let reduceMotionEnabled = prefersReducedMotion.matches
    const removeReducedMotionListener = addMediaQueryListener(
      prefersReducedMotion,
      () => {
        reduceMotionEnabled = prefersReducedMotion.matches
        scheduleFrame()
      },
    )

    let currentProgress01 = 0
    let frameId: number | null = null

    const setCssVar = (element: HTMLElement, name: string, value: string) => {
      element.style.setProperty(name, value)
    }

    const computeTargetProgress01 = (): number => {
      const sentinelTopPx = sentinel.getBoundingClientRect().top
      const distancePastStartPx = Math.max(
        0,
        -sentinelTopPx - collapseStartOffsetPx,
      )
      return clamp01(distancePastStartPx / safeCollapseDistancePx)
    }

    const applyStyles = (progress01: number) => {
      const eased = easeOutCubic(progress01)

      const availableWidthPx = stickyRoot.getBoundingClientRect().width
      if (!Number.isFinite(availableWidthPx) || availableWidthPx <= 0) return

      const expandedWidthPx = availableWidthPx
      const collapsedWidthPx = Math.min(
        safeCollapsedMaxWidthPx,
        expandedWidthPx,
      )

      const widthPx = lerp(expandedWidthPx, collapsedWidthPx, eased)
      const paddingPx = lerp(0, safeCollapsedPaddingPx, eased)

      setCssVar(stickyRoot, CSS_VARS.backgroundAlpha, eased.toFixed(4))
      setCssVar(
        animatedContainer,
        CSS_VARS.containerWidth,
        `${widthPx.toFixed(2)}px`,
      )
      setCssVar(
        animatedContainer,
        CSS_VARS.containerPadding,
        `${paddingPx.toFixed(2)}px`,
      )
    }

    const step = () => {
      frameId = null

      const targetProgress01 = computeTargetProgress01()
      const jumpToTarget = reduceMotionEnabled

      if (jumpToTarget) {
        currentProgress01 = targetProgress01
        applyStyles(currentProgress01)
        return
      }

      currentProgress01 =
        currentProgress01 +
        (targetProgress01 - currentProgress01) * safeSmoothingFactor

      applyStyles(currentProgress01)

      if (Math.abs(targetProgress01 - currentProgress01) > 0.001) {
        scheduleFrame()
      }
    }

    const scheduleFrame = () => {
      if (frameId !== null) return
      frameId = window.requestAnimationFrame(step)
    }

    scheduleFrame()
    window.addEventListener('scroll', scheduleFrame, { passive: true })
    window.addEventListener('resize', scheduleFrame)

    return () => {
      if (frameId !== null) window.cancelAnimationFrame(frameId)
      window.removeEventListener('scroll', scheduleFrame)
      window.removeEventListener('resize', scheduleFrame)
      removeReducedMotionListener()
    }
  }, [
    sentinelRef,
    stickyRootRef,
    animatedContainerRef,
    collapsedMaxWidthPx,
    collapsedPaddingPx,
    collapseStartOffsetPx,
    collapseDistancePx,
    smoothingFactor,
  ])
}
